import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, Shield, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useConsent } from "@/context/ConsentContext";

export default function CookieBanner() {
  const { consent, settingsOpen, acceptAll, rejectAll, savePreferences, openSettings, closeSettings } = useConsent();
  const [analytics, setAnalytics] = useState(false);
  const loc = useLocation();
  // Im Kundenbereich (mobile Bottom-Nav) den Banner oberhalb der Navigationsleiste platzieren.
  const inApp = loc.pathname.startsWith("/app");

  useEffect(() => {
    if (consent) setAnalytics(!!consent.analytics);
  }, [consent, settingsOpen]);

  // Öffentliches Wallboard (/live/:token) ist ein unbeaufsichtigter Vollbildschirm –
  // dort KEIN Cookie-Banner (niemand klickt, es verdeckt Inhalte).
  if (loc.pathname.startsWith("/live/")) return null;
  if (!consent) return null;
  const showBanner = !consent.decided && !settingsOpen;

  return (
    <>
      {showBanner && (
        <div className={`fixed inset-x-0 z-[60] px-3 pb-3 sm:px-6 sm:pb-4 ${inApp ? "bottom-16 lg:bottom-0" : "bottom-0"}`} data-testid="cookie-banner">
          <div className="max-w-3xl mx-auto bg-white/95 backdrop-blur-sm border border-fc-border rounded-xl shadow-[0_16px_40px_-16px_rgba(19,36,58,0.3)] p-3 sm:px-4 sm:py-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-start gap-2.5 min-w-0 flex-1">
                <Cookie className="w-5 h-5 text-fc-blue flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                <p className="text-[13px] text-fc-ink/65 leading-snug">
                  Nur technisch notwendige Speicherung; mit Ihrer Einwilligung zusätzlich anonyme Statistik (PostHog), jederzeit widerrufbar.{" "}
                  <Link to="/datenschutz" className="text-fc-blue underline">Datenschutz</Link>.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={rejectAll} className="h-9 px-3 rounded-lg border border-fc-border bg-white text-sm font-medium text-fc-ink/80 hover:border-fc-ink/30 transition-colors" data-testid="cookie-reject">Nur notwendige</button>
                <button onClick={openSettings} className="h-9 px-3 rounded-lg text-sm font-medium text-fc-ink/60 hover:text-fc-ink transition-colors" data-testid="cookie-settings-open">Einstellungen</button>
                <button onClick={acceptAll} className="h-9 px-4 rounded-lg bg-fc-blue text-white text-sm font-semibold hover:bg-fc-blue/90 transition-colors" data-testid="cookie-accept">Alle akzeptieren</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog open={settingsOpen} onOpenChange={(o) => (o ? openSettings() : closeSettings())}>
        <DialogContent className="max-w-lg" data-testid="cookie-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Datenschutz-Einstellungen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 rounded-xl bg-fc-subtle/50 border border-fc-border">
              <Shield className="w-5 h-5 text-fc-mint flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-fc-ink flex items-center justify-between">
                  Notwendig <span className="text-xs text-fc-ink/50">Immer aktiv</span>
                </div>
                <p className="text-sm text-fc-ink/60 mt-1">Login-Sitzung und das Speichern deiner Datenschutz-Auswahl. Ohne diese funktioniert NEWZER nicht.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 rounded-xl bg-white border border-fc-border">
              <BarChart3 className="w-5 h-5 text-fc-blue flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-fc-ink flex items-center justify-between">
                  Statistik & Analyse
                  <Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="cookie-toggle-analytics" />
                </div>
                <p className="text-sm text-fc-ink/60 mt-1">Anonyme Produktanalyse und Session-Aufzeichnung (PostHog), um die Bedienung zu verbessern.</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end mt-2">
            <Button variant="outline" onClick={rejectAll} className="rounded-xl border-fc-border bg-white h-11" data-testid="cookie-settings-reject">
              Alle ablehnen
            </Button>
            <Button onClick={() => savePreferences(analytics)} className="rounded-xl bg-fc-blue text-white h-11 font-semibold" data-testid="cookie-settings-save">
              Auswahl speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
